import { Box, Container } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { Link } from 'react-router-dom';
import { getFeaturedEbook } from '../repository';
import { KC_COLORS } from '../theme';

function EbookLink({ href, className, style, children }) {
  if (!href) return null;
  return (
    <Link to={href} className={className} style={style}>
      {children}
    </Link>
  );
}

export function FeaturedEbook() {
  const section = getFeaturedEbook();
  if (!section) return null;

  return (
    <Box
      id="kc-ebook"
      className="kc-ebook w-full py-8 md:py-10 scroll-mt-28"
      style={{ backgroundColor: KC_COLORS.white, textAlign: 'left' }}
    >
      <Container maxWidth="lg">
        <div
          className="kc-ebook-card rounded-[24px] border overflow-hidden shadow-[0_10px_36px_rgba(10,29,58,0.08)]"
          style={{ borderColor: KC_COLORS.border, textAlign: 'left' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div
              className="lg:col-span-5 relative px-5 py-7 md:px-8 md:py-9 lg:px-10 lg:py-10"
              style={{
                background:
                  'linear-gradient(160deg, #071526 0%, #0A1D3A 55%, #0D3A4A 100%)',
              }}
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                  background:
                    'radial-gradient(ellipse at 20% 10%, rgba(0,155,170,0.28) 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(94,234,212,0.16) 0%, transparent 50%)',
                }}
                aria-hidden
              />

              {section.badge ? (
                <span
                  className="relative inline-flex items-center rounded-md px-2.5 py-1 kc-type-caption font-bold uppercase tracking-wide"
                  style={{
                    backgroundColor: KC_COLORS.teal,
                    color: KC_COLORS.white,
                  }}
                >
                  {section.badge}
                </span>
              ) : null}

              <p
                className="relative m-0 mt-4 uppercase tracking-[0.1em]"
                style={{
                  color: 'rgba(255,255,255,0.65)',
                  fontSize: '12px',
                  lineHeight: '16px',
                  fontWeight: 700,
                }}
              >
                Executive Ebook
              </p>

              <h2
                className="kc-section-title relative m-0 mt-2"
                style={{
                  color: KC_COLORS.white,
                  fontSize: '28px',
                  lineHeight: '36px',
                  fontWeight: 700,
                  textAlign: 'left',
                }}
              >
                {section.title}
              </h2>

              <div className="relative mt-8 flex items-center justify-center py-4">
                <MenuBookOutlinedIcon
                  sx={{
                    fontSize: 88,
                    color: '#5EEAD4',
                    filter: 'drop-shadow(0 0 18px rgba(94,234,212,0.45))',
                  }}
                />
              </div>

              <div
                className="relative mt-auto flex flex-wrap gap-x-4 gap-y-2"
                style={{
                  color: 'rgba(255,255,255,0.75)',
                  fontSize: '13px',
                  lineHeight: '18px',
                }}
              >
                {section.chapterCount ? (
                  <span>{section.chapterCount} chapters</span>
                ) : null}
                {section.partCount ? <span>{section.partCount} parts</span> : null}
                {section.readTimeLabel ? (
                  <span>{section.readTimeLabel}</span>
                ) : null}
              </div>
            </div>

            <div
              className="lg:col-span-7 bg-white px-5 py-7 md:px-8 md:py-9 lg:px-10 lg:py-10"
              style={{ textAlign: 'left' }}
            >
              {section.description ? (
                <p
                  className="m-0"
                  style={{
                    color: KC_COLORS.gray600,
                    fontSize: '15px',
                    lineHeight: '24px',
                    textAlign: 'left',
                  }}
                >
                  {section.description}
                </p>
              ) : null}

              {section.highlights?.length ? (
                <ul className="m-0 mt-5 mb-6 p-0 list-none flex flex-col gap-3">
                  {section.highlights.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3"
                      style={{
                        color: KC_COLORS.primaryNavy,
                        fontSize: '15px',
                        lineHeight: '22px',
                        textAlign: 'left',
                      }}
                    >
                      <span
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5"
                        style={{ backgroundColor: KC_COLORS.teal }}
                      >
                        <CheckRoundedIcon
                          sx={{ fontSize: 14, color: KC_COLORS.white }}
                        />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="flex flex-wrap gap-3">
                {section.primaryCta ? (
                  <EbookLink
                    href={section.primaryCta.href || section.href}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-5 py-2.5 font-semibold no-underline transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: KC_COLORS.teal,
                      color: KC_COLORS.white,
                      fontSize: '15px',
                      lineHeight: '22px',
                    }}
                  >
                    {section.primaryCta.label}
                    <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                  </EbookLink>
                ) : null}
                {section.secondaryCta ? (
                  <EbookLink
                    href={
                      section.secondaryCta.href || section.firstChapterHref
                    }
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-5 py-2.5 font-semibold no-underline transition-colors"
                    style={{
                      border: `1.5px solid ${KC_COLORS.teal}`,
                      color: KC_COLORS.primaryNavy,
                      backgroundColor: KC_COLORS.white,
                      fontSize: '15px',
                      lineHeight: '22px',
                    }}
                  >
                    {section.secondaryCta.label}
                    <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                  </EbookLink>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Box>
  );
}
